// src/pages/fiscal/mdfe/MDFeConsultaPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaDownload, FaEdit, FaEye, FaFilePdf, FaSearch } from 'react-icons/fa';
import Input from '../../../components/input/Input';
import Select from '../../../components/input/Select';
import PageContainer from '../../../components/geral/PageContainer';
import { useMessage } from '../../../contexts/geral/MessageContext';
import { useYesNo } from '../../../contexts/geral/YesNoContext';
import { useMDFe } from '../../../contexts/fiscal/MDFeContext';
import { SITUACOES_MDFE, getAcoesMDFe, getSituacaoMDFe } from '../../../models/fiscal/MDFe';
import MDFeDetalhesModal from './MDFeDetalhesModal';
import MDFeAcaoModal from './MDFeAcaoModal';
import './MDFe.css';

const hoje = () => new Date().toISOString().slice(0, 10);
const inicioMes = () => `${hoje().slice(0, 8)}01`;
const formatar = (valor) => valor ? new Date(valor).toLocaleString('pt-BR') : '—';
const moeda = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const MDFeConsultaPage = () => {
  const api = useMDFe();
  const navigate = useNavigate();
  const { showMessage } = useMessage();
  const { showYesNo } = useYesNo();
  const [filtros, setFiltros] = useState({ filialId: 1, situacao: '', dataInicial: inicioMes(), dataFinal: hoje(), serie: '', numDoc: '', chave: '', pagina: 1, tamanhoPagina: 20 });
  const [resultado, setResultado] = useState({ itens: [], total: 0, pagina: 1, tamanhoPagina: 20 });
  const [detalhe, setDetalhe] = useState(null);
  const [acao, setAcao] = useState(null);
  const alterar = (campo, valor) => setFiltros((prev) => ({ ...prev, [campo]: valor, pagina: campo === 'pagina' ? valor : 1 }));

  const buscar = async () => {
    const result = await api.listar(filtros);
    if (!result) return;
    setResultado({
      itens: result.itens ?? [],
      total: result.total ?? 0,
      pagina: result.pagina ?? filtros.pagina,
      tamanhoPagina: result.tamanhoPagina ?? filtros.tamanhoPagina,
    });
  };
  useEffect(() => { if (filtros.filialId) buscar(); }, [filtros.filialId, filtros.pagina, filtros.tamanhoPagina]);

  const atualizarItem = (item) => {
    if (!item?.id) return false;
    setResultado((prev) => ({
      ...prev,
      itens: (prev.itens ?? []).map((registro) => registro?.id === item.id ? item : registro),
    }));
    return true;
  };
  const consultar = async (item) => {
    const result = await api.consultar(item.id);
    if (result) atualizarItem(result);
  };
  const abrirDetalhe = async (item) => {
    const result = await api.obter(item.id);
    if (result) setDetalhe(result);
  };

  const excluir = async (item) => {
    if (await showYesNo(`Deseja excluir o rascunho ${item.id}?`)) {
      await api.excluir(item.id);
      buscar();
    }
  };

  const confirmarAcao = async (dados) => {
    try {
      if (!acao?.item?.id) {
        showMessage('Selecione um MDF-e válido para executar a ação.', 'warning');
        setAcao(null);
        return;
      }

      let result;
      if (acao.tipo === 'cancelar') {
        if ((dados.justificativa || '').trim().length < 15) return showMessage('A justificativa deve possuir ao menos 15 caracteres.', 'warning');
        result = await api.cancelar(acao.item.id, dados.justificativa.trim());
      } else if (acao.tipo === 'encerrar') {
        if (!dados.dataEncerramento || !dados.codigoMunicipio || !dados.uf) return showMessage('Preencha data, município e UF.', 'warning');
        result = await api.encerrar(acao.item.id, { dataEncerramento: dados.dataEncerramento, codigoMunicipio: Number(dados.codigoMunicipio), uf: dados.uf });
      } else {
        const cpf = String(dados.cpf || '').replace(/\D/g, '');
        if (!dados.nome?.trim() || cpf.length !== 11) return showMessage('Informe nome e CPF válidos.', 'warning');
        result = await api.incluirCondutor(acao.item.id, { nome: dados.nome.trim(), cpf });
      }
      if (!result) return;
      if (!atualizarItem(result)) await buscar();
      setAcao(null);
    } catch (error) {
      console.error('Erro inesperado ao executar ação do MDF-e:', error);
      showMessage('Não foi possível concluir a ação do MDF-e.', 'danger');
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(resultado.total / resultado.tamanhoPagina));
  return <PageContainer><div className="mdfe-page">
    <header className="mdfe-titlebar"><div><h2><FaSearch /> Consulta de MDF-e</h2><span>Filtros fiscais, eventos e documentos autorizados</span></div></header>
    <section className="mdfe-card"><div className="mdfe-filterbar">
      <Select label="Situação" value={filtros.situacao} size={170} options={[{ value: '', label: 'Todas' }, ...SITUACOES_MDFE.map((s) => ({ value: s.value, label: s.label }))]} onChange={(e) => alterar('situacao', e.target.value)} />
      <Input label="Data inicial" type="date" value={filtros.dataInicial} onChange={(e) => alterar('dataInicial', e.target.value)} size={135} />
      <Input label="Data final" type="date" value={filtros.dataFinal} onChange={(e) => alterar('dataFinal', e.target.value)} size={135} />
      <Input label="Série" type="number" value={filtros.serie} onChange={(e) => alterar('serie', e.target.value)} size={80} />
      <Input label="Número" type="number" value={filtros.numDoc} onChange={(e) => alterar('numDoc', e.target.value)} size={100} />
      <Input label="Chave" value={filtros.chave} onChange={(e) => alterar('chave', e.target.value)} size={270} />
      <Select label="Por página" value={filtros.tamanhoPagina} size={100} options={[10, 20, 50].map((n) => ({ value: n, label: n }))} onChange={(e) => alterar('tamanhoPagina', Number(e.target.value))} />
      <button className="btn btn-primary" disabled={api.loading} onClick={buscar}><FaSearch /> Buscar</button>
    </div></section>
    <section className="mdfe-card"><div className="mdfe-table-wrap"><table className="mdfe-table"><thead><tr><th>Série/Número</th><th>Emissão</th><th>Trajeto</th><th>NF-e</th><th>Valor/Peso</th><th>Chave</th><th>Situação</th><th>Último retorno</th><th>Ações</th></tr></thead><tbody>
      {resultado.itens?.map((item) => { const status = getSituacaoMDFe(item.situacao); const acoes = getAcoesMDFe(item.situacao); return <tr key={item.id}><td>{item.serie ?? '—'}/{item.numDoc ?? '—'}</td><td>{formatar(item.dataEmissao)}</td><td>{item.ufInicio} → {item.ufFim}</td><td>{item.notas?.length || 0}</td><td>{moeda(item.valorCarga)}<br />{item.quantidadeCarga} {Number(item.unidadeCarga) === 2 ? 'TON' : 'KG'}</td><td className="mdfe-key">{item.chave || '—'}</td><td><span className={`badge bg-${status.className}`}>{status.label}</span></td><td>{item.codigoStatus ?? '—'}<br />{item.motivoStatus || '—'}</td><td><div className="mdfe-actionbar">
        <button title="Detalhes" className="btn btn-sm btn-outline-secondary" onClick={() => abrirDetalhe(item)}><FaEye /></button>
        {acoes.editar && <button title="Editar" className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/mdfe/emitir?id=${item.id}`)}><FaEdit /></button>}
        {acoes.consultar && <button className="btn btn-sm btn-outline-primary" onClick={() => consultar(item)}>Consultar</button>}
        {acoes.excluir && <button className="btn btn-sm btn-outline-danger" onClick={() => excluir(item)}>Excluir</button>}
        {acoes.cancelar && <button className="btn btn-sm btn-outline-danger" onClick={() => setAcao({ tipo: 'cancelar', item })}>Cancelar</button>}
        {acoes.encerrar && <button className="btn btn-sm btn-outline-success" onClick={() => setAcao({ tipo: 'encerrar', item })}>Encerrar</button>}
        {acoes.incluirCondutor && <button className="btn btn-sm btn-outline-info" onClick={() => setAcao({ tipo: 'condutor', item })}>Condutor</button>}
        {acoes.arquivos && <><button title="Baixar XML" className="btn btn-sm btn-outline-secondary" onClick={() => api.baixarXml(item.id)}><FaDownload /></button><button title="Abrir DAMDFE" className="btn btn-sm btn-outline-secondary" onClick={() => api.abrirDamdfe(item.id)}><FaFilePdf /></button></>}
      </div></td></tr>; })}
      {!resultado.itens?.length && <tr><td colSpan="9" className="mdfe-empty">Nenhum MDF-e encontrado.</td></tr>}
    </tbody></table></div>
      <div className="d-flex justify-content-between align-items-center mt-3"><span>{resultado.total} registro(s)</span><div className="btn-group"><button className="btn btn-sm btn-outline-secondary" disabled={filtros.pagina <= 1} onClick={() => alterar('pagina', filtros.pagina - 1)}>Anterior</button><button className="btn btn-sm btn-light" disabled>{filtros.pagina} de {totalPaginas}</button><button className="btn btn-sm btn-outline-secondary" disabled={filtros.pagina >= totalPaginas} onClick={() => alterar('pagina', filtros.pagina + 1)}>Próxima</button></div></div>
    </section>
  </div>
  <MDFeDetalhesModal show={!!detalhe} item={detalhe} onHide={() => setDetalhe(null)} />
  <MDFeAcaoModal show={!!acao} tipo={acao?.tipo} loading={api.loading} onHide={() => setAcao(null)} onConfirm={confirmarAcao} />
  </PageContainer>;
};
export default MDFeConsultaPage;
